import React from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '@benzinga/core-ui';
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { faChartLine } from '@fortawesome/pro-solid-svg-icons/faChartLine';
import { faPhone } from '@fortawesome/pro-solid-svg-icons/faPhone';
import { faCoins } from '@fortawesome/pro-solid-svg-icons/faCoins';
import { faSplit } from '@fortawesome/pro-solid-svg-icons/faSplit';
import { faHandshake } from '@fortawesome/pro-solid-svg-icons/faHandshake';
import { faExternalLink } from '@fortawesome/pro-solid-svg-icons/faExternalLink';
import type { StockInfoMessage } from '../../utils/quote/stock-info-message-generator';

interface StockInfoMessagesProps {
  messages: StockInfoMessage[];
}

const MESSAGE_ICONS: Record<string, IconDefinition> = {
  'stock-split': faSplit,
  dividend: faCoins,
  earnings: faChartLine,
  'conference-call': faPhone,
  ma: faHandshake,
};

export const StockInfoMessages: React.FC<StockInfoMessagesProps> = ({ messages }) => {
  const { t } = useTranslation('quote');

  if (!Array.isArray(messages) || messages.length === 0) {
    return null;
  }

  const MESSAGE_LABELS: Record<string, string> = {
    'stock-split': t('Quote.Reports.StockInfoLabels.stock-split'),
    dividend: t('Quote.Reports.StockInfoLabels.dividend'),
    earnings: t('Quote.Reports.StockInfoLabels.earnings'),
    'conference-call': t('Quote.Reports.StockInfoLabels.conference-call'),
    ma: t('Quote.Reports.StockInfoLabels.m-and-a'),
  };

  return (
    <div className="flex w-full flex-col gap-1 md:gap-1.5">
      {messages.map((message, index) => {
        return (
          <div
            className="flex w-full items-start gap-1.5 rounded bg-blue-500 px-2.5 py-1.5 text-[13px] leading-[1.4] text-white md:px-3 md:py-2 md:text-sm"
            key={index}
          >
            <Icon className="mt-0.5 shrink-0 text-[13px]" icon={MESSAGE_ICONS[message.type] || faChartLine} />
            <div className="min-w-0 flex-1">
              <span className="font-bold">{MESSAGE_LABELS[message.type] || message.type}: </span>
              <span>{message.content}</span>
              {message.type === 'conference-call' && message.webcastUrl && (
                <a
                  className="ml-1.5 inline-flex items-center gap-1 font-medium text-inherit underline hover:opacity-90 max-md:ml-0 max-md:mt-1 max-md:flex"
                  href={message.webcastUrl}
                  rel="noopener noreferrer"
                  target="_blank"
                >
                  <Icon className="text-[11px]" icon={faExternalLink} />
                  {message.webcastUrl.includes('past') || message.webcastUrl.includes('archive')
                    ? t('Quote.Reports.ConferenceCall.click-to-view-past-webcast')
                    : t('Quote.Reports.ConferenceCall.click-to-view-the-webcast')}
                </a>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};
